import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { api } from '../services/api';
import { useToast } from '../components/ui/Toast';
import { formatDate } from '../utils/helpers';
import type { User, UserMatch, Twin } from '../types';
import { Layout } from '../components/layout/Layout';
import { TwinList } from '../components/users/TwinList';
import { DeleteUserModal } from '../components/users/DeleteUserModal';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Modal } from '../components/ui/Modal';
import { getInitials } from '../utils/helpers';
import {
  ArrowLeft,
  Mail,
  Calendar,
  MapPin,
  Briefcase,
  GraduationCap,
  Ban,
  Check,
  Bot,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Trash2,
} from 'lucide-react';

function MatchDetailRow({ match }: { match: UserMatch }) {
  const [expanded, setExpanded] = useState(false);
  const pct = match.matchPercentage;
  const barColor = pct >= 80 ? '#22c55e' : pct >= 60 ? '#3b82f6' : '#f59e0b';
  const badgeVariant = pct >= 80 ? 'success' : pct >= 60 ? 'info' : 'warning';

  return (
    <div className="border border-[#2a2a3a] rounded-xl overflow-hidden">
      <button
        className="w-full text-left p-3 hover:bg-[#12121a] transition-colors"
        onClick={() => setExpanded(e => !e)}
      >
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="min-w-0">
              <div className="flex items-center gap-1">
                <Bot className="w-3 h-3 text-[#3b82f6] shrink-0" />
                <span className="text-sm font-medium text-[#f8fafc] truncate">{match.myTwin.name}</span>
              </div>
              <p className="text-xs text-[#64748b]">Your Twin</p>
            </div>
            <ArrowRight className="w-3.5 h-3.5 text-[#64748b] shrink-0" />
            <div className="min-w-0">
              <div className="flex items-center gap-1">
                <Bot className="w-3 h-3 text-[#a855f7] shrink-0" />
                <span className="text-sm font-medium text-[#f8fafc] truncate">{match.matchedTwin.name}</span>
              </div>
              <p className="text-xs text-[#64748b]">{match.matchedUser.name}'s Twin</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Badge variant={badgeVariant}>{pct}% Match</Badge>
            {expanded
              ? <ChevronUp className="w-4 h-4 text-[#64748b]" />
              : <ChevronDown className="w-4 h-4 text-[#64748b]" />}
          </div>
        </div>

        {/* Match bar */}
        <div className="mt-2 h-1 bg-[#2a2a3a] rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${pct}%`, backgroundColor: barColor }}
          />
        </div>
      </button>

      {expanded && (
        <div className="px-3 pb-3 border-t border-[#2a2a3a] pt-3 bg-[#0f0f1a] space-y-2">
          {match.summary ? (
            <>
              <p className="text-xs font-semibold text-[#64748b] uppercase tracking-wider">AI Summary</p>
              <div className="bg-[#141420] border border-[#252535] rounded-lg p-3 max-h-28 overflow-y-auto">
                <p className="text-sm text-[#94a3b8] leading-relaxed whitespace-pre-line">{match.summary}</p>
              </div>
            </>
          ) : (
            <p className="text-xs text-[#64748b] italic">No summary available</p>
          )}
          <p className="text-xs text-[#64748b]">Matched {formatDate(match.createdAt)}</p>
        </div>
      )}
    </div>
  );
}

export function UserDetail() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [user, setUser] = useState<User | null>(null);
  const [twins, setTwins] = useState<Twin[]>([]);
  const [matches, setMatches] = useState<UserMatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [updating, setUpdating] = useState(false);

  const loadUserPage = useCallback(async () => {
    if (!userId) return;
    const [userData, matchesData, twinsData] = await Promise.all([
      api.getUser(userId),
      api.getUserMatches(userId),
      api.getUserTwins(userId),
    ]);
    setUser(userData);
    setMatches(matchesData.matches);
    setTwins(twinsData.twins);
  }, [userId]);

  useEffect(() => {
    const run = async () => {
      if (!userId) return;
      try {
        setLoading(true);
        await loadUserPage();
      } catch (err) {
        console.error('Failed to load user:', err);
        toast(err instanceof Error ? err.message : 'Failed to load user', 'error');
      } finally {
        setLoading(false);
      }
    };
    void run();
  }, [toast, userId, loadUserPage]);
  const handleToggleStatus = async () => {
    if (!user) return;

    try {
      setUpdating(true);
      await api.updateUserStatus(user.id, !user.disabled);
      setUser({ ...user, disabled: !user.disabled });
      setShowDisableModal(false);
      toast(
        `${user.name} has been ${user.disabled ? 'enabled' : 'disabled'} successfully`,
        'success'
      );
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to update user status.', 'error');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-2 border-[#3b82f6] border-t-transparent rounded-full" />
        </div>
      </Layout>
    );
  }

  if (!user) {
    return (
      <Layout>
        <div className="text-center py-16 text-[#94a3b8]">
          User not found
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Back Button */}
        <button
          onClick={() => navigate('/users')}
          className="flex items-center gap-2 text-[#94a3b8] hover:text-[#f8fafc] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Users
        </button>

        {/* Profile Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            {user.avatar ? (
              <img
                src={user.avatar}
                alt={user.name}
                className="w-16 h-16 rounded-full object-cover"
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-[#1a1a24] border border-[#2a2a3a] flex items-center justify-center text-xl font-medium text-[#f8fafc]">
                {getInitials(user.name)}
              </div>
            )}
            <div>
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-bold text-[#f8fafc]">{user.name}</h2>
                <Badge variant={user.disabled ? 'error' : 'success'}>
                  {user.disabled ? 'Disabled' : 'Active'}
                </Badge>
              </div>
              <div className="flex items-center gap-4 mt-1 text-[#94a3b8]">
                <span className="flex items-center gap-1.5">
                  <Mail className="w-4 h-4" />
                  {user.email}
                </span>
                <span className="flex items-center gap-1.5">
                  <Calendar className="w-4 h-4" />
                  Joined {formatDate(user.createdAt)}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant={user.disabled ? 'secondary' : 'danger'}
              onClick={() => setShowDisableModal(true)}
              icon={user.disabled ? <Check className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
            >
              {user.disabled ? 'Enable Account' : 'Disable Account'}
            </Button>
            {!user.isAdmin && (
              <Button
                variant="secondary"
                onClick={() => setShowDeleteModal(true)}
                icon={<Trash2 className="w-4 h-4" />}
                className="text-[#f87171] hover:text-[#fca5a5]"
              >
                Delete User
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile Info */}
          <div className="lg:col-span-1 space-y-6">
            <Card>
              <CardHeader title="Profile Information" />
              <div className="space-y-4">
                <div>
                  <p className="text-xs text-[#64748b] uppercase tracking-wide">User Type</p>
                  <div className="mt-1">
                    <Badge variant={user.userType === 'company' ? 'info' : 'success'}>
                      {user.userType === 'company' ? 'Company' : 'Job Seeker'}
                    </Badge>
                  </div>
                </div>

                {user.profile?.occupation && (
                  <div className="flex items-start gap-3">
                    <Briefcase className="w-4 h-4 text-[#64748b] mt-0.5" />
                    <div>
                      <p className="text-xs text-[#64748b] uppercase tracking-wide">Occupation</p>
                      <p className="text-sm text-[#f8fafc] mt-0.5">{user.profile.occupation}</p>
                    </div>
                  </div>
                )}

                {user.profile?.educationLevel && (
                  <div className="flex items-start gap-3">
                    <GraduationCap className="w-4 h-4 text-[#64748b] mt-0.5" />
                    <div>
                      <p className="text-xs text-[#64748b] uppercase tracking-wide">Education</p>
                      <p className="text-sm text-[#f8fafc] mt-0.5">{user.profile.educationLevel}</p>
                    </div>
                  </div>
                )}

                {(user.profile?.city || user.profile?.country) && (
                  <div className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 text-[#64748b] mt-0.5" />
                    <div>
                      <p className="text-xs text-[#64748b] uppercase tracking-wide">Location</p>
                      <p className="text-sm text-[#f8fafc] mt-0.5">
                        {[user.profile.city, user.profile.country].filter(Boolean).join(', ')}
                      </p>
                    </div>
                  </div>
                )}

                {/* Sign-up form details */}
                <div className="pt-4 border-t border-[#2a2a3a] space-y-3">
                  <p className="text-xs text-[#64748b] uppercase tracking-wide">Sign-up Details</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                    <div>
                      <p className="text-[10px] text-[#64748b] uppercase tracking-wide">Date of Birth</p>
                      <p className="text-sm text-[#f8fafc] mt-0.5">
                        {user.profile?.dateOfBirth ? formatDate(user.profile.dateOfBirth) : '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] text-[#64748b] uppercase tracking-wide">Gender</p>
                      <p className="text-sm text-[#f8fafc] mt-0.5 capitalize">
                        {user.profile?.gender || '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] text-[#64748b] uppercase tracking-wide">Student</p>
                      <p className="text-sm text-[#f8fafc] mt-0.5">
                        {user.profile?.isStudent ? 'Yes' : 'No'}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] text-[#64748b] uppercase tracking-wide">State</p>
                      <p className="text-sm text-[#f8fafc] mt-0.5">{user.profile?.state || '-'}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-[10px] text-[#64748b] uppercase tracking-wide">Address</p>
                      <p className="text-sm text-[#f8fafc] mt-0.5">{user.profile?.address || '-'}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-[#64748b] uppercase tracking-wide">Credits</p>
                      <p className="text-sm text-[#f8fafc] mt-0.5">{user.profile?.credits ?? 0}</p>
                    </div>
                    {user.profile?.profilePicture && (
                      <div className="col-span-2">
                        <p className="text-[10px] text-[#64748b] uppercase tracking-wide">Profile Picture</p>
                        <a
                          href={user.profile.profilePicture}
                          target="_blank"
                          rel="noreferrer"
                          className="text-sm text-[#3b82f6] hover:underline break-all"
                        >
                          {user.profile.profilePicture}
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                {user.profile?.bio && (
                  <div className="pt-4 border-t border-[#2a2a3a]">
                    <p className="text-xs text-[#64748b] uppercase tracking-wide">Bio</p>
                    <p className="text-sm text-[#f8fafc] mt-0.5">{user.profile.bio}</p>
                  </div>
                )}
              </div>
            </Card>

            {/* Stats Card */}
            <Card>
              <CardHeader title="Activity Stats" />
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-[#12121a] rounded-lg">
                  <p className="text-2xl font-bold text-[#f8fafc]">{user.twinCount}</p>
                  <p className="text-sm text-[#94a3b8]">Twins Created</p>
                </div>
                <div className="p-4 bg-[#12121a] rounded-lg">
                  <p className="text-2xl font-bold text-[#f8fafc]">{matches.length}</p>
                  <p className="text-sm text-[#94a3b8]">Total Matches</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Twins and Matches */}
          <div className="lg:col-span-2 space-y-6">
            <TwinList
              userId={user.id}
              twins={twins}
              onTwinsChanged={async () => {
                try {
                  await loadUserPage();
                } catch (err) {
                  toast(err instanceof Error ? err.message : 'Failed to refresh user data', 'error');
                }
              }}
            />

            <Card>
              <CardHeader
                title={`Matches (${matches.length})`}
                subtitle="All twin matches for this user"
              />
              {matches.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <Bot className="w-8 h-8 text-[#64748b] mb-2" />
                  <p className="text-sm text-[#94a3b8]">No matches yet</p>
                  <p className="text-xs text-[#64748b] mt-1">Matches appear once twins connect</p>
                </div>
              ) : (
                <div className="space-y-2 mt-3">
                  {matches.map((match) => (
                    <MatchDetailRow key={match.matchId} match={match} />
                  ))}
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>

      <DeleteUserModal
        user={showDeleteModal ? user : null}
        onClose={() => setShowDeleteModal(false)}
        onDeleted={() => navigate('/users', { replace: true })}
      />

      {/* Disable/Enable Modal */}
      <Modal
        isOpen={showDisableModal}
        onClose={() => setShowDisableModal(false)}
        title={user.disabled ? 'Enable User' : 'Disable User'}
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowDisableModal(false)}>
              Cancel
            </Button>
            <Button
              variant={user.disabled ? 'primary' : 'danger'}
              onClick={handleToggleStatus}
              loading={updating}
            >
              {user.disabled ? 'Enable User' : 'Disable User'}
            </Button>
          </>
        }
      >
        <p className="text-[#94a3b8]">
          Are you sure you want to {user.disabled ? 'enable' : 'disable'}{' '}
          <span className="text-[#f8fafc] font-medium">{user.name}</span>?
        </p>
        <p className="text-[#64748b] text-sm mt-2">
          {user.disabled
            ? 'This will restore the user\'s access to the platform.'
            : 'This will prevent the user from accessing the platform.'}
        </p>
      </Modal>
    </Layout>
  );
}
